import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronLeftIcon, EnterIcon } from '@radix-ui/react-icons';
import { Button, Card, Flex, Spinner } from '@radix-ui/themes';
import { isEmpty } from 'lodash';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import CheckboxComponent from '../../components/inputs/Checkbox/CheckboxComponent';
import RadioGroupComponent from '../../components/inputs/RadioGroup/RadioGroupComponent';
import SelectComponent from '../../components/inputs/Select/SelectComponent';
import TextFieldComponent from '../../components/inputs/Textfield/TextfieldComponent';
import { useGetDepartamentsQuery } from '../../hooks/useGetDepartamentsQuery';
import { useGetFacultiesQuery } from '../../hooks/useGetFacultiesQuery';
import { useGetKnowledgeDomainsQuery } from '../../hooks/useGetKnowledgeDomainsQuery';
import { useGetKnowledgeSubDomainsQuery } from '../../hooks/useGetKnowledgeSubDomainsQuery';
import { useGetUniversitiesQuery } from '../../hooks/useGetUniversitiesQuery';
import { routes } from '../../routes';
import { createCourse } from '../../services/school/course/createCourse';
import { CreateCourseType } from '../../types/Course';
import { CourseLevels, Modalities } from '../../utils/codes';
import { getFile } from '../../utils/fileUtils';
import { CreateCourseFormValidation } from './CreateCourseFormValidation';

export default function CreateCourse() {
  const navigate = useNavigate();
  const mutation = useMutation({ mutationFn: createCourse, mutationKey: 'createCourse' });

  const defaultValues: CreateCourseType = {
    description: '',
    duration: 0,
    month: 0,
    day: 0,
    name: '',
    idDepartament: '',
    typeDuration: 'Years',
    idUniversity: '',
    idFaculty: '',
    level: 'Licenciatura',
    modality: '1',
    isPreAccredited: true,
    idKnowledgeSubDomain: '',
    idKnowledgeDomain: '',
    code: ''
  }
  const { control, handleSubmit, setValue, getValues, formState: { errors }, resetField, watch } = useForm<CreateCourseType>({
    resolver: yupResolver(CreateCourseFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const { data: universities, isLoading: isLoadingUniversities, isError: errorUniversities, refetch: refetchUniversities } = useGetUniversitiesQuery({ PageNumber: 1, PageSize: 400 });
  const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties, refetch: refetchFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400, IdUniversity: getValues('idUniversity') });
  const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery(
    { PageNumber: 1, PageSize: 400, IdFaculty: getValues('idFaculty') },
    { enabled: !!getValues('idFaculty') }
  );
  const { data: domains, isLoading: isLoadingDomains, isError: errorDomains } = useGetKnowledgeDomainsQuery({ PageNumber: 1, PageSize: 400 });
  const { data: subDomains, isLoading: isLoadingSubDomains, isError: errorSubDomains, refetch: refreshSubDomains } = useGetKnowledgeSubDomainsQuery(
    { PageNumber: 1, PageSize: 400, DomainId: getValues('idKnowledgeDomain') },
    { enabled: !!getValues('idKnowledgeDomain') }
  );

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (data?.file) {
        const { base64 } = await getFile(data.file)
        data.docBase64 = base64;
      } else {
        data.docBase64 = '';
      }
      const result = await mutation.mutateAsync(data);
      if (result) {
        toast.success("(Curso / Programa) criado");
        navigate(routes.courses);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });

  const handleBack = () => {
    navigate(routes.courses);
  }

  const handleFacultyChange = (idFaculty: string) => {
    setValue('idFaculty', idFaculty);
    refreshDepartaments();
  };
  const handleKnowledgeDomainChange = (id: string) => {
    setValue('idKnowledgeDomain', id);
    if (isEmpty(id)) {
      resetField('idKnowledgeSubDomain')
      return;
    }
    refreshSubDomains();
  };
  const watchTypeDuration = watch('typeDuration')
  const yearsEnabled = watchTypeDuration === 'Years';
  const monthsEnabled = watchTypeDuration === 'Months';
  const daysEnabled = watchTypeDuration === 'Days';
  const handleCourseNameChange = (e: any) => {
    const name = e.target.value;
    setValue("name", name);
    setValue("description", name);
  }

  const handleOnChangeTypeDuration = (e: any) => {
    const typeDuraction = e.target.value;
    setValue('typeDuration', typeDuraction);
    switch (typeDuraction) {
      case 'Years': {
        resetField('day')
        resetField('month')
      } break;
      case 'Months': {
        resetField('day')
        resetField('duration') //years
      } break;
      case 'Days': {
        resetField('month')
        resetField('duration') //years
      } break;
    }

  };
  const handleUniversityChange = (id: string) => {
    setValue('idUniversity', id);
    setValue('idFaculty', '');
    setValue('idDepartament', '');
    refetchFaculties();
  };
  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4' style={{ overflow: 'auto' }}>
          <Flex gap="8">
            <TextFieldComponent name="name" label="Nome" control={control} errors={errors} fieldProps={{ placeholder: 'Ex: Engenharia Informática', onChange: handleCourseNameChange }} style={{ width: '100%' }} required={true} />
          </Flex>
          <Flex gap='4'>
            <TextFieldComponent name="code" label="Código do curso" control={control} errors={errors} fieldProps={{ placeholder: 'Ex: CS505' }} style={{ width: '100%' }} required={true} />
          </Flex>

          <Flex gap='4'>
            <CheckboxComponent name="isPreAccredited" label="Acreditado? " control={control} errors={errors} required onChange={(checked) => console.log("Checkbox está:", checked)} />
          </Flex>

          <Flex gap="8">
            <RadioGroupComponent name="typeDuration" label="Duração do curso/programa" control={control} errors={errors} style={{ width: '100%' }}
              options={[
                { value: "Days", label: "Dias" },
                { value: "Months", label: "Meses" },
                { value: "Years", label: "Anos" }
              ]}
              fieldProps={{
                style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' },
                onChange: handleOnChangeTypeDuration
              }}
            />
          </Flex>

          <Flex gap='4'>
            <TextFieldComponent name="day" label="Duração do curso (Dias)" control={control} errors={errors} fieldProps={{ placeholder: 'Duração do curso (Dias)', type: 'number', disabled: !daysEnabled }} style={{ width: '100%' }} />
            <TextFieldComponent name="month" label="Duração do curso (Meses)" control={control} errors={errors} fieldProps={{ placeholder: 'Duração do curso (Meses)', type: 'number', disabled: !monthsEnabled }} style={{ width: '100%' }} />
            <TextFieldComponent name="duration" label="Duração do curso (Anos)" control={control} errors={errors} fieldProps={{ placeholder: 'Duração do curso (Anos)', type: 'number', disabled: !yearsEnabled }} style={{ width: '100%' }} />
          </Flex>

          <Flex gap='2'>
            <SelectComponent
              name="idKnowledgeDomain"
              label="Domínio de Conhecimento"
              control={control}
              errors={errors}
              style={{ width: '100%' }}
              options={domains?.items.map(domain => ({
                value: domain.id ?? "",
                label: domain.description ?? ""
              }))}
              itemsLoading={isLoadingDomains}
              itemsError={errorDomains}
              onSelect={handleKnowledgeDomainChange}
              required={true}

            />
            <SelectComponent
              name="idKnowledgeSubDomain"
              label="SubDomínio de Conhecimento"
              control={control}
              errors={errors}
              style={{ width: '100%' }}
              options={subDomains?.items.map(domain => ({
                value: domain.id ?? "",
                label: domain.description ?? ""
              }))}
              itemsLoading={isLoadingSubDomains}
              itemsError={errorSubDomains}
              required={true}
            />
          </Flex>
          <SelectComponent
            name="idUniversity"
            label="Instituição de Ensino Superior"
            control={control}
            errors={errors}
            style={{ width: '100%' }}
            options={universities?.items.map(u => ({
              value: u.id ?? "",
              label: u.description ?? ""
            }))}
            itemsLoading={isLoadingUniversities}
            itemsError={errorUniversities}
            onSelect={handleUniversityChange}
            required={true}
          />
          <SelectComponent name="idFaculty" label="Unidade orgânica" control={control} errors={errors} style={{ width: '100%' }}
            options={faculties?.items.map(faculty => ({
              value: faculty.id ?? "",
              label: faculty.description ?? ""
            }))}
            onSelect={handleFacultyChange}
          />

          <SelectComponent name="idDepartament" label="Departamento" control={control} errors={errors} style={{ width: '100%' }}
            options={departaments?.items.map(departament => ({
              value: departament.id ?? "",
              label: departament.description ?? ""
            }))}
          />

          <Flex gap="8">
            <RadioGroupComponent name="level" label="Nível" control={control} errors={errors} style={{ width: '100%' }}
              options={CourseLevels.map(cl => ({
                value: cl.id ?? "",
                label: cl.description ?? ""
              }))}
              fieldProps={{
                style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
              }}
            />
          </Flex>
          <Flex gap="8">
            <RadioGroupComponent name="modality" label="Modalidade" control={control} errors={errors} style={{ width: '100%' }}
              options={Modalities.map(m => ({
                value: m.id ?? "",
                label: m.description ?? ""
              }))}
              fieldProps={{
                style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
              }}
            />
          </Flex>


          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button type='button' variant='soft' color='gray' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> Voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'>
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  );
}